import { WIDGET_ACTION_FILTERS } from '@/utils/widgetActionFilters';

/** Scenario metadata for footer copy generation */
const SCENARIO = {
  'incident.escalated_reportable': {
    short: 'reportable escalated',
    singular: '1 reportable incident escalated',
    plural: (n) => `${n} reportable incidents escalated`,
  },
  'incident.escalated': {
    short: 'escalated',
    singular: '1 incident escalated',
    plural: (n) => `${n} incidents escalated`,
  },
  'incident.changes_requested': {
    short: 'need your input',
    singular: '1 incident needs your input',
    plural: (n) => `${n} incidents need your input`,
  },
  'incident.pending_manager_review': {
    short: 'pending manager review',
    singular: '1 incident pending manager review',
    plural: (n) => `${n} incidents pending manager review`,
  },
  'incident.pending_review': {
    short: 'pending compliance review',
    singular: '1 incident pending compliance review',
    plural: (n) => `${n} incidents pending compliance review`,
  },
  'incident.requires_action': {
    short: 'need action',
    singular: '1 incident needs action',
    plural: (n) => `${n} incidents need action`,
  },
  'compliance.expired': {
    short: 'expired',
    singular: '1 compliance document expired',
    plural: (n) => `${n} compliance documents expired`,
  },
  'compliance.expiring_soon': {
    short: 'expiring this week',
    singular: '1 document expiring this week',
    plural: (n) => `${n} documents expiring this week`,
  },
  'compliance.pending_review': {
    short: 'pending review',
    singular: '1 document pending review',
    plural: (n) => `${n} documents pending review`,
  },
  'policy.review_overdue': {
    short: 'overdue',
    singular: '1 policy review overdue',
    plural: (n) => `${n} policy reviews overdue`,
  },
  'policy.review_due': {
    short: 'due',
    singular: '1 policy review due',
    plural: (n) => `${n} policy reviews due`,
  },
  'invoice.overdue': {
    short: 'overdue',
    singular: '1 invoice overdue',
    plural: (n) => `${n} invoices overdue`,
  },
  'invoice.due_soon': {
    short: 'due this week',
    singular: '1 invoice due this week',
    plural: (n) => `${n} invoices due this week`,
  },
  'invoice.due_later': {
    short: 'due soon',
    singular: '1 invoice due soon',
    plural: (n) => `${n} invoices due soon`,
  },
  'quote.draft': {
    short: 'ready to send',
    singular: '1 quote ready to send',
    plural: (n) => `${n} quotes ready to send`,
  },
  'quote.awaiting_response': {
    short: 'awaiting response',
    singular: '1 quote awaiting response',
    plural: (n) => `${n} quotes awaiting response`,
  },
  'service_agreement.draft': {
    short: 'in draft',
    singular: '1 service agreement in draft',
    plural: (n) => `${n} service agreements in draft`,
  },
  'service_agreement.awaiting_signature': {
    short: 'awaiting signature',
    singular: '1 agreement awaiting signature',
    plural: (n) => `${n} agreements awaiting signature`,
  },
  'recruitment.interview.schedule': {
    short: 'to schedule',
    singular: '1 interview to schedule',
    plural: (n) => `${n} interviews to schedule`,
  },
  'recruitment.interview.upcoming': {
    short: 'upcoming',
    singular: '1 upcoming interview',
    plural: (n) => `${n} upcoming interviews`,
  },
  'recruitment.offer.draft': {
    short: 'draft offers',
    singular: '1 offer draft to complete',
    plural: (n) => `${n} offer drafts to complete`,
  },
  'recruitment.offer.ready_to_send': {
    short: 'ready to send',
    singular: '1 offer ready to send',
    plural: (n) => `${n} offers ready to send`,
  },
  'recruitment.offer.awaiting_acceptance': {
    short: 'awaiting acceptance',
    singular: '1 offer awaiting acceptance',
    plural: (n) => `${n} offers awaiting acceptance`,
  },
  'recruitment.reference.not_sent': {
    short: 'not sent',
    singular: '1 reference check not sent',
    plural: (n) => `${n} reference checks not sent`,
  },
  'recruitment.reference.awaiting': {
    short: 'awaiting response',
    singular: '1 reference awaiting response',
    plural: (n) => `${n} references awaiting response`,
  },
  'recruitment.reference.in_progress': {
    short: 'in progress',
    singular: '1 reference in progress',
    plural: (n) => `${n} references in progress`,
  },
  'recruitment.reference.expired': {
    short: 'expired',
    singular: '1 reference request expired',
    plural: (n) => `${n} reference requests expired`,
  },
  'leave.pending_approval': {
    short: 'pending approval',
    singular: '1 leave request pending approval',
    plural: (n) => `${n} leave requests pending approval`,
  },
  'esignature.expired': {
    short: 'expired',
    singular: '1 signature request expired',
    plural: (n) => `${n} signature requests expired`,
  },
  'esignature.in_progress': {
    short: 'in progress',
    singular: '1 signature in progress',
    plural: (n) => `${n} signatures in progress`,
  },
  'esignature.awaiting': {
    short: 'awaiting signature',
    singular: '1 signature awaiting',
    plural: (n) => `${n} signatures awaiting`,
  },
};

const WIDGET_PRIORITY = {
  incidents: [
    'incident.escalated_reportable',
    'incident.escalated',
    'incident.changes_requested',
    'incident.pending_manager_review',
    'incident.pending_review',
    'incident.requires_action',
  ],
  compliance_documents: ['compliance.expired', 'compliance.expiring_soon', 'compliance.pending_review'],
  policy: ['policy.review_overdue', 'policy.review_due'],
  invoice_followups: ['invoice.overdue', 'invoice.due_soon', 'invoice.due_later'],
  quotes: ['quote.draft', 'quote.awaiting_response'],
  service_agreements: ['service_agreement.awaiting_signature', 'service_agreement.draft'],
  jobs: [
    'recruitment.offer.draft',
    'recruitment.offer.ready_to_send',
    'recruitment.offer.awaiting_acceptance',
    'recruitment.interview.schedule',
    'recruitment.interview.upcoming',
  ],
  reference_checks: [
    'recruitment.reference.expired',
    'recruitment.reference.not_sent',
    'recruitment.reference.awaiting',
    'recruitment.reference.in_progress',
  ],
  leave: ['leave.pending_approval'],
  esignatures: ['esignature.expired', 'esignature.awaiting', 'esignature.in_progress'],
};

const WIDGET_NOUN = {
  incidents: 'incidents',
  compliance_documents: 'documents',
  policy: 'policy reviews',
  invoice_followups: 'invoices',
  quotes: 'quotes',
  service_agreements: 'agreements',
  jobs: 'recruitment items',
  reference_checks: 'reference checks',
  leave: 'leave requests',
  esignatures: 'signatures',
};

function inferScenarioKey(action) {
  if (action.scenario_key) return action.scenario_key;

  const type = action.action_type;
  const status = (action.entity_status || '').toLowerCase();
  const desc = (action.description || '').toLowerCase();

  if (type === 'incident.requires_action') {
    if (status === 'escalated') {
      return action.reportable_incident ? 'incident.escalated_reportable' : 'incident.escalated';
    }
    if (status === 'changes_requested') return 'incident.changes_requested';
    if (status === 'pending_manager_review') return 'incident.pending_manager_review';
    if (status === 'pending_review') return 'incident.pending_review';
    return 'incident.requires_action';
  }
  if (type === 'compliance.document') {
    if (status === 'expired') return 'compliance.expired';
    if (desc.includes('expiring')) return 'compliance.expiring_soon';
    return 'compliance.pending_review';
  }
  if (type === 'invoice.due') {
    if (desc.includes('overdue')) return 'invoice.overdue';
    if (desc.includes('soon')) return 'invoice.due_soon';
    return 'invoice.due_later';
  }
  if (type === 'quote.action') {
    return status === 'draft' ? 'quote.draft' : 'quote.awaiting_response';
  }
  if (type === 'policy.review') {
    return desc.includes('overdue') ? 'policy.review_overdue' : 'policy.review_due';
  }
  if (type === 'esignature') {
    if (status === 'expired') return 'esignature.expired';
    if (status === 'in_progress' || status === 'partially_signed') return 'esignature.in_progress';
    return 'esignature.awaiting';
  }
  return type || 'unknown';
}

export function filterWidgetActions(actions = [], widgetId) {
  const filter = WIDGET_ACTION_FILTERS[widgetId];
  if (!filter || !Array.isArray(actions)) return [];

  return actions.filter((action) => {
    if (filter.module && action.module !== filter.module) return false;
    if (filter.actionTypes?.length && !filter.actionTypes.includes(action.action_type)) {
      return false;
    }
    return true;
  });
}

function groupByScenario(actions) {
  const groups = {};
  for (const action of actions) {
    const key = inferScenarioKey(action);
    groups[key] = (groups[key] || 0) + 1;
  }
  return groups;
}

function pickWinner(groups, priority = []) {
  for (const key of priority) {
    if (groups[key]) return { key, count: groups[key] };
  }
  const entries = Object.entries(groups);
  if (!entries.length) return null;
  const [key, count] = entries[0];
  return { key, count };
}

function scenarioMeta(key) {
  return SCENARIO[key] || {
    short: 'need action',
    singular: '1 item needs action',
    plural: (n) => `${n} items need action`,
  };
}

/** Incidents: total on winner — e.g. "3 incidents need action · 1 escalated" */
function formatIncidentsFooter(total, groups) {
  const winner = pickWinner(groups, WIDGET_PRIORITY.incidents);
  if (!winner) return `${total} incidents need action`;

  const meta = scenarioMeta(winner.key);
  if (total === 1) return meta.singular;
  if (total === winner.count) return meta.plural(total);
  return `${total} incidents need action · ${winner.count} ${meta.short}`;
}

function formatGenericFooter(widgetId, total, groups) {
  const priority = WIDGET_PRIORITY[widgetId] || [];
  const winner = pickWinner(groups, priority);
  if (!winner) {
    const noun = WIDGET_NOUN[widgetId] || 'items';
    return `${total} ${noun} need action`;
  }

  const meta = scenarioMeta(winner.key);
  if (total === 1) return meta.singular;
  if (total === winner.count) return meta.plural(total);

  const noun = WIDGET_NOUN[widgetId] || 'items';
  return `${total} ${noun} need action · ${winner.count} ${meta.short}`;
}

/**
 * Build personalized footer label for a dashboard widget from Action Centre items.
 */
export function getWidgetActionFooterLabel(actions = [], widgetId) {
  const filtered = filterWidgetActions(actions, widgetId);
  const total = filtered.length;
  if (total <= 0) return null;

  const groups = groupByScenario(filtered);

  if (widgetId === 'incidents') {
    return formatIncidentsFooter(total, groups);
  }

  return formatGenericFooter(widgetId, total, groups);
}

export function getWidgetActionCountFromActions(actions = [], widgetId) {
  return filterWidgetActions(actions, widgetId).length;
}
